import type { FunctionComponent } from 'react';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import {
  paletteHelper,
  spacingHelper,
  typographyHelper,
  Text,
  mediaHelper,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { InternetConnectionBrick } from './subcomponents/InternetConnectionBrick/InternetConnectionBrick';
import {
  ButtonFragment,
  TextFragment,
  WebLinkFragment,
} from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import { BadgeVariant } from '../../../app-kit/helpers/mapBadgeVariant';
import { OriginCTABanner } from './subcomponents/OriginCTABanner/OriginCTABanner';
import { mapApiWeblinkVariantToLinkVariant } from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';

export type ConnectionQuality =
  | 'ADEQUATE'
  | 'ADEQUATE_ELIGIBLE'
  | 'AMAZING'
  | 'EXCELLENT'
  | 'EXCELLENT_ELIGIBLE'
  | 'LIMITED'
  | 'LIMITED_UNTHEMED';

const DisclaimerText = styled(Text)`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('extraSmall')};
`;

const ConnectionBrickWrapper = styled.div`
  display: flex;
  gap: 0.5rem;

  ${mediaHelper('tabletMax')} {
    flex-direction: column;
  }
`;

interface InternetAvailabilityBrickPropsV2 {
  title: string;
  subtitle: string;
  disclaimer: string;
  currentConnection: {
    title: string;
    subtitle: string | null;
    badge?: {
      text: string;
      variant: BadgeVariant;
    } | null;
    description: (WebLinkFragment | TextFragment | ButtonFragment)[];
    connectionQuality: ConnectionQuality | null;
  };
  eligibleConnection: {
    title: string;
    subtitle: string | null;
    badge: {
      text: string;
      variant: BadgeVariant;
    };
    description: (WebLinkFragment | TextFragment | ButtonFragment)[];
    connectionQuality: ConnectionQuality;
  } | null;
  originCTABanner: OriginCTABanner | null;
}

const InternetAvailabilityBrickV2: FunctionComponent<InternetAvailabilityBrickPropsV2> = ({
  title,
  subtitle,
  disclaimer,
  currentConnection,
  eligibleConnection,
  originCTABanner,
}) => {
  return (
    <ContentBrick title={title} subtitle={subtitle}>
      <ConnectionBrickWrapper>
        <InternetConnectionBrick connection={currentConnection} />
        {eligibleConnection && <InternetConnectionBrick connection={eligibleConnection} />}
      </ConnectionBrickWrapper>
      {originCTABanner && <OriginCTABanner {...originCTABanner} />}
      <DisclaimerText>{disclaimer}</DisclaimerText>
    </ContentBrick>
  );
};

export const InternetAvailabilityBrickWithDataV2: FunctionComponent = () => {
  const internetAvailability =
    usePropertyPageQuery()?.data.propertyPageBySlugV3.internetAvailabilityV2;
  if (!internetAvailability) return null;

  const { title, subtitle, disclaimer, nbnConnection, eligibleConnection, originCTABanner } =
    internetAvailability;

  return (
    <InternetAvailabilityBrickV2
      title={title}
      subtitle={subtitle}
      disclaimer={disclaimer}
      currentConnection={nbnConnection}
      eligibleConnection={eligibleConnection}
      originCTABanner={
        originCTABanner
          ? {
              title: originCTABanner.title,
              subtitle: originCTABanner.subtitle,
              ctaButton: {
                title: originCTABanner.ctaButton.title,
                url: originCTABanner.ctaButton.url,
                isExternal: originCTABanner.ctaButton.isExternal,
                variant: mapApiWeblinkVariantToLinkVariant(originCTABanner.ctaButton.variant),
                tracking: originCTABanner.ctaButton.tracking,
              },
              connectionType: originCTABanner.currentConnection,
              upgradeAvailable: originCTABanner.upgradeAvailable,
            }
          : null
      }
    />
  );
};
